/**
 * Privacy Policy Page
 * ===================
 * Privacy policy and data handling practices.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Card } from '@/components/ui/Card';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/register"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Registration
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
            <Shield className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-gray-600">Last updated: March 12, 2026</p>
        </div>

        {/* Content */}
        <Card className="p-8 prose prose-sm max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy describes how Dentulu ("we," "us," or "our") collects, uses, and protects your personal 
              information when you use the Dentulu Periodontal Charting System ("Service"). We are committed to protecting 
              your privacy and maintaining the security of your data, including Protected Health Information (PHI).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">2.1 Account Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Name, email address, and contact information</li>
              <li>Organization/practice details</li>
              <li>Professional credentials and license information</li>
              <li>Account preferences and settings</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">2.2 Patient Information (PHI)</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Patient demographics and contact information</li>
              <li>Medical and dental history</li>
              <li>Periodontal measurements and chart data</li>
              <li>Clinical notes and observations</li>
              <li>Treatment records and history</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">2.3 Usage Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Log data including IP address, browser type, and access times</li>
              <li>Device information and operating system</li>
              <li>Features used and interaction data</li>
              <li>Performance and error logs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>To provide and maintain the Service</li>
              <li>To enable periodontal charting and patient management</li>
              <li>To improve and optimize the Service</li>
              <li>To provide customer support</li>
              <li>To send service updates and important notices</li>
              <li>To ensure security and prevent fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement robust security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>End-to-end encryption for data in transit</li>
              <li>Encryption at rest for stored data</li>
              <li>Multi-factor authentication options</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and role-based permissions</li>
              <li>Regular backups and disaster recovery procedures</li>
              <li>HIPAA-compliant infrastructure and practices</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>With your consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service providers:</strong> Third-party vendors who assist in providing the Service (all under appropriate data protection agreements)</li>
              <li><strong>Legal requirements:</strong> When required by law, court order, or regulatory authority</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Protection:</strong> To protect rights, property, or safety of Dentulu, users, or others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data (subject to legal retention requirements)</li>
              <li><strong>Export:</strong> Receive your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Restriction:</strong> Request restriction of data processing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your information for as long as necessary to provide the Service and comply with legal obligations. 
              Patient health information is retained in accordance with HIPAA requirements and applicable state laws, 
              typically for a minimum of 7 years after the last patient interaction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are accessing the Service from outside the United States, please be aware that your information may be 
              transferred to, stored, and processed in the United States where our servers are located. We ensure appropriate 
              safeguards are in place for international transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service is not intended for use by individuals under 18 years of age. We do not knowingly collect personal 
              information from children. If you become aware that a child has provided us with personal information, please 
              contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the 
              new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service after 
              changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Privacy Officer</strong></p>
              <p className="text-gray-700">Email: privacy@dentulu.com</p>
              <p className="text-gray-700">Phone: 1-800-DENTULU</p>
              <p className="text-gray-700 mt-2">
                Mailing Address:<br />
                Dentulu Privacy Office<br />
                123 Healthcare Drive<br />
                San Francisco, CA 94102
              </p>
            </div>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              This Privacy Policy is effective as of the date stated at the top of this page.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
